import type { AuditBatch, AuditEvent } from '@repo/sdk/gen'
import { zAuditBatch, zAuditEvent } from '@repo/sdk/zod'
import { formatBytes } from '@repo/ui/utils'
import type { TFunction, TKey } from '@/lib/i18n'

export type AuditAction = AuditEvent['action']

export type AuditBatchKind = AuditBatch['kind']

/**
 * The product's audit catalogue, read off the contract rather than retyped
 * beside it. The category filter's options are built from here, so an entry a
 * later release starts emitting reaches the filter without anyone editing a
 * second list — and reaches every map below as a compile error, which is the
 * point.
 *
 * The catalogue also holds *planned* entries the backend deliberately keeps out
 * of this enum: an omission reads as "we do not track that", a silent enum
 * member reads as "we do", and only one of the two is true of work that has not
 * landed. So this list is what the product writes today, and nothing else.
 */
export const AUDIT_ACTIONS = zAuditEvent.shape.action.options

/**
 * The batch catalogue, the same reasoning as {@link AUDIT_ACTIONS} applied to
 * the accumulating summary rows rather than the single recorded acts: read
 * off the contract, total by construction below, and including the three
 * kinds MS16 has not started writing yet — `trash`, `restore`,
 * `version_restore` are declared in the wire type today so that a client
 * built against it does not need a second release to recognise them once a
 * writer exists (OPSSTD-8136).
 */
export const AUDIT_BATCH_KINDS = zAuditBatch.shape.kind.options

/**
 * Action → the words an administrator reads for the Sheet's action field and
 * (until this milestone) the table. **Total by construction**:
 * `satisfies Record<AuditAction, TKey>` makes an action without a label a
 * compile error, so no row can arrive here with no reading and fall back to
 * printing `share.role_changed` at somebody. `TKey` checks the other end — a
 * key absent from the catalogues does not typecheck either.
 */
export const AUDIT_ACTION_LABEL_KEYS = {
	'authz.denied': 'admin-audit:action.authz_denied',
	'policy.changed': 'admin-audit:action.policy_changed',
	'audit.retention_applied': 'admin-audit:action.audit_retention_applied',
	'share.granted': 'admin-audit:action.share_granted',
	'share.role_changed': 'admin-audit:action.share_role_changed',
	'share.revoked': 'admin-audit:action.share_revoked',
	'group.created': 'admin-audit:action.group_created',
	'group.updated': 'admin-audit:action.group_updated',
	'group.deleted': 'admin-audit:action.group_deleted',
	'group.member_added': 'admin-audit:action.group_member_added',
	'group.member_removed': 'admin-audit:action.group_member_removed',
	'link.accessed': 'admin-audit:action.link_accessed',
	'org_link.changed': 'admin-audit:action.org_link_changed',
	'org_link.opened': 'admin-audit:action.org_link_opened',
	'link.created': 'admin-audit:action.link_created',
	'link.updated': 'admin-audit:action.link_updated',
	'link.revoked': 'admin-audit:action.link_revoked',
	'link.previewed': 'admin-audit:action.link_previewed',
	'link.downloaded': 'admin-audit:action.link_downloaded',
	'link.password_locked': 'admin-audit:action.link_password_locked',
	'link.upload_limited': 'admin-audit:action.link_upload_limited',
	'upload.expired': 'admin-audit:action.upload_expired',
	'node.upload_reconciled': 'admin-audit:action.node_upload_reconciled',
	// `node.trashed`, `node.restored` and `node.version_restored` are retired
	// (OPSSTD-8136, MS16's conversion): they are `AuditBatchKind` members now,
	// in {@link AUDIT_BATCH_KIND_LABEL_KEYS} below, not `AuditAction`.
	// `node.purged` stays here — a permanent destruction is exactly the single
	// row an administrator needs to find on its own, not a count to
	// accumulate.
	'node.purged': 'admin-audit:action.node_purged',
	'node.version_deleted': 'admin-audit:action.node_version_deleted',
	'node.versions_expired': 'admin-audit:action.node_versions_expired',
	'user.invited': 'admin-audit:action.user_invited',
	'user.invite_accepted': 'admin-audit:action.user_invite_accepted',
	'user.invite_resent': 'admin-audit:action.user_invite_resent',
	'user.removed': 'admin-audit:action.user_removed',
	'user.email_changed': 'admin-audit:action.user_email_changed',
	'user.role_changed': 'admin-audit:action.user_role_changed',
	'user.owner_granted': 'admin-audit:action.user_owner_granted',
	'user.owner_revoked': 'admin-audit:action.user_owner_revoked',
	'user.quota_changed': 'admin-audit:action.user_quota_changed',
	'organization.created': 'admin-audit:action.organization_created',
	'organization.suspended': 'admin-audit:action.organization_suspended',
	'organization.reactivated': 'admin-audit:action.organization_reactivated',
	'organization.quota_changed':
		'admin-audit:action.organization_quota_changed',
	'organization.version_retention_changed':
		'admin-audit:action.organization_version_retention_changed',
	'organization.storage_destroyed':
		'admin-audit:action.organization_storage_destroyed',
	'session.revoked': 'admin-audit:action.session_revoked',
	'user.credentials_changed': 'admin-audit:action.user_credentials_changed',
	'session.force_disconnected':
		'admin-audit:action.session_force_disconnected',
	'user.deactivated': 'admin-audit:action.user_deactivated',
	'user.reactivated': 'admin-audit:action.user_reactivated',
	'user.membership_removed': 'admin-audit:action.user_membership_removed'
} as const satisfies Record<AuditAction, TKey>

/**
 * Batch kind → the name the details panel gives a batch row, built like an
 * event's ("Link creato"): what happened, with how many —
 * «3 elementi spostati nel cestino». A bare noun ("Cestino") named a place
 * rather than an act. Pluralised on `filesSucceeded`, the count the table's
 * sentence uses, so the two agree. Total by construction, same reasoning as
 * {@link AUDIT_ACTION_LABEL_KEYS}.
 */
export const AUDIT_BATCH_KIND_LABEL_KEYS = {
	upload: 'admin-audit:batch_kind.upload',
	download: 'admin-audit:batch_kind.download',
	preview: 'admin-audit:batch_kind.preview',
	trash: 'admin-audit:batch_kind.trash',
	restore: 'admin-audit:batch_kind.restore',
	version_restore: 'admin-audit:batch_kind.version_restore',
	copy: 'admin-audit:batch_kind.copy'
} as const satisfies Record<AuditBatchKind, TKey>

/**
 * Batch kind → the table's "Cosa" verb phrase, pluralised on `filesSucceeded`
 * the way `user_invite_resent` is pluralised on its own count — the base key
 * alone, so `t()` resolves `_one`/`_other` itself. Deliberately not a full
 * {@link AuditSentence} builder like {@link AUDIT_ACTION_SENTENCE}: a batch's
 * row is a verb phrase plus three facts a translated string cannot carry —
 * how many failed, how many bytes, what span of time — each formatted through
 * `useLocaleFormat`/`formatBytes` rather than through i18next, so the cell
 * composes them itself instead of a builder that cannot reach those hooks.
 */
export const AUDIT_BATCH_KIND_SENTENCE_KEYS = {
	upload: 'admin-audit:batch_sentence.upload',
	download: 'admin-audit:batch_sentence.download',
	preview: 'admin-audit:batch_sentence.preview',
	trash: 'admin-audit:batch_sentence.trash',
	restore: 'admin-audit:batch_sentence.restore',
	version_restore: 'admin-audit:batch_sentence.version_restore',
	copy: 'admin-audit:batch_sentence.copy'
} as const satisfies Record<AuditBatchKind, TKey>

/**
 * Whether the act succeeded. Read by the Sheet's outcome badge — the table
 * itself no longer carries a column for it since MS10's redesign: a failure
 * colours the whole Cosa sentence `text-destructive` instead, because a badge
 * beside a truncated sentence is easy to miss and the colour is not.
 */
export const AUDIT_OUTCOME_LABEL_KEYS = {
	success: 'admin-audit:outcome.success',
	failure: 'admin-audit:outcome.failure'
} as const satisfies Record<AuditEvent['outcome'], TKey>

/**
 * Where the request came from. `system` is not a client: it is the absence of
 * one, and the word has to say so rather than name a channel.
 */
export const AUDIT_ORIGIN_LABEL_KEYS = {
	web: 'admin-audit:origin.web',
	api: 'admin-audit:origin.api',
	system: 'admin-audit:origin.system'
} as const satisfies Record<AuditEvent['origin'], TKey>

/**
 * What kind of thing was acted on.
 *
 * **Open, not exhaustive, and the type says so.** `objectType` is a plain
 * string in the contract, not an enum — the five values below are what the
 * product files today (`user`, `organization`, `policy`, `node`, `group`) and
 * a later one arrives with the milestone that starts writing it. A row
 * carrying one this map does not hold renders the raw
 * value, which is a technical word rather than a wrong one; the alternative —
 * pretending the set is closed — would either drop the column's meaning or
 * print a missing translation key.
 */
export const AUDIT_OBJECT_TYPE_LABEL_KEYS: Record<string, TKey> = {
	user: 'admin-audit:object_type.user',
	organization: 'admin-audit:object_type.organization',
	policy: 'admin-audit:object_type.policy',
	node: 'admin-audit:object_type.node',
	group: 'admin-audit:object_type.group'
}

/**
 * The five groups the Tipo filter offers, coarser than {@link AuditAction}
 * because nobody narrows an investigation to `node.upload_reconciled` — they
 * narrow it to "content". The URL carries these, never a raw action: `types[]`
 * survives a future action landing inside an existing group with no
 * migration, where `actions[]` would have needed one.
 */
export const AUDIT_CATEGORIES = [
	'people',
	'access',
	'sharing',
	'content',
	'organization'
] as const

export type AuditCategory = (typeof AUDIT_CATEGORIES)[number]

export const AUDIT_CATEGORY_LABEL_KEYS = {
	people: 'admin-audit:category.people',
	access: 'admin-audit:category.access',
	sharing: 'admin-audit:category.sharing',
	content: 'admin-audit:category.content',
	organization: 'admin-audit:category.organization'
} as const satisfies Record<AuditCategory, TKey>

/**
 * Action → category. **Total by construction**, the same reasoning as
 * {@link AUDIT_ACTION_LABEL_KEYS}: an action the catalogue starts emitting and
 * nobody assigns a group to is a compile error here, not a row silently
 * excluded from every category a person could pick.
 */
export const AUDIT_ACTION_CATEGORY = {
	'authz.denied': 'access',
	'policy.changed': 'organization',
	'audit.retention_applied': 'organization',
	'share.granted': 'sharing',
	'share.role_changed': 'sharing',
	'share.revoked': 'sharing',
	// Who is in which group is a question about the people, the same one
	// «Persone» answers; what a group can reach is filed under its shares.
	'group.created': 'people',
	'group.updated': 'people',
	'group.deleted': 'people',
	'group.member_added': 'people',
	'group.member_removed': 'people',
	'link.accessed': 'sharing',
	'org_link.changed': 'sharing',
	'org_link.opened': 'sharing',
	'link.created': 'sharing',
	'link.updated': 'sharing',
	'link.revoked': 'sharing',
	'link.previewed': 'sharing',
	'link.downloaded': 'sharing',
	'link.password_locked': 'sharing',
	'link.upload_limited': 'sharing',
	'upload.expired': 'content',
	'node.upload_reconciled': 'content',
	'node.purged': 'content',
	'node.version_deleted': 'content',
	'node.versions_expired': 'content',
	'user.invited': 'people',
	'user.invite_accepted': 'people',
	'user.invite_resent': 'people',
	'user.removed': 'people',
	'user.email_changed': 'people',
	'user.role_changed': 'people',
	'user.owner_granted': 'people',
	'user.owner_revoked': 'people',
	'user.quota_changed': 'people',
	'organization.created': 'organization',
	'organization.suspended': 'organization',
	'organization.reactivated': 'organization',
	'organization.quota_changed': 'organization',
	'organization.version_retention_changed': 'organization',
	'organization.storage_destroyed': 'organization',
	'session.revoked': 'access',
	'user.credentials_changed': 'access',
	'session.force_disconnected': 'access',
	'user.deactivated': 'people',
	'user.reactivated': 'people',
	'user.membership_removed': 'people'
} as const satisfies Record<AuditAction, AuditCategory>

/**
 * The actions behind a set of picked categories, sorted — so two administrators
 * picking the same two categories in a different click order still produce the
 * same query key, and the same cache entry.
 */
export function auditActionsForCategories(
	categories: AuditCategory[]
): AuditAction[] {
	return AUDIT_ACTIONS.filter((action) =>
		categories.includes(AUDIT_ACTION_CATEGORY[action])
	).toSorted()
}

/**
 * The batch kinds behind a set of picked categories — every one of them,
 * because every batch kind is a transfer on content and there is only the one
 * category to file it under. Unlike {@link auditActionsForCategories} this is
 * not a per-kind lookup: a batch carries no catalogue action to key one by, so
 * the whole set moves together with "content" rather than being split further.
 *
 * Returns the same reference every time content is among the categories,
 * which keeps the query key stable across renders the way the sorted actions
 * array already does.
 */
export function auditBatchKindsForCategories(
	categories: AuditCategory[]
): AuditBatchKind[] {
	return categories.includes('content') ? AUDIT_BATCH_KINDS : []
}

/** The tenant role a share or a membership carries, read off metadata. */
function roleLabel(role: unknown, t: TFunction) {
	if (role === 'org_admin') {
		return t('admin-audit:role.org_admin')
	}
	if (role === 'member') {
		return t('admin-audit:role.member')
	}
	// A role this map does not hold is a technical word rather than a wrong
	// one — the same reasoning `AUDIT_OBJECT_TYPE_LABEL_KEYS` carries for an
	// object type it does not recognise.
	return typeof role === 'string' ? role : ''
}

function metaString(event: AuditEvent, key: string) {
	const value = event.metadata?.[key]
	return typeof value === 'string' ? value : undefined
}

function metaNumber(event: AuditEvent, key: string) {
	const value = event.metadata?.[key]
	return typeof value === 'number' ? value : undefined
}

function metaBoolean(event: AuditEvent, key: string) {
	const value = event.metadata?.[key]
	return typeof value === 'boolean' ? value : undefined
}

/**
 * A storage quota off metadata, where null is a value of its own: no limit at
 * all, which is where every organization and every person starts.
 */
function quotaLabel(event: AuditEvent, key: string, t: TFunction) {
	const value = event.metadata?.[key]
	if (typeof value === 'number') {
		return formatBytes(value)
	}
	return value === null ? t('admin-audit:quota.no_limit') : '?'
}

/**
 * A person's name for a sentence, degraded to the same declared placeholder
 * the Chi column uses — never the id. `null` is normal here, not exceptional:
 * see `AuditActorCell`'s note on why.
 */
function personLabel(name: string | null, t: TFunction) {
	return name ?? t('admin-audit:actor.unknown')
}

/**
 * Who a share was made to. A group's row carries `subjectType: 'group'` and no
 * resolved name (`subjectName` resolves people only), so it reads «a group»
 * rather than the placeholder for a person who left.
 */
function shareSubjectLabel(event: AuditEvent, t: TFunction) {
	return metaString(event, 'subjectType') === 'group'
		? t('admin-audit:subject.group')
		: personLabel(event.subjectName, t)
}

/** One rendered sentence: an i18n key plus the values it interpolates. */
export interface AuditSentence {
	key: TKey
	values?: Record<string, unknown>
}

/**
 * Builds a typed {@link AuditSentence} rather than a bare object literal.
 *
 * Every builder below returns through this rather than `{ key, values }`
 * directly: without an explicit return type on each of the arrow functions
 * below, TypeScript infers each one's own narrow literal type —
 * `{ key: "…authz_denied" }` has no `values` property at all where another
 * branch's does — and indexing the map by a runtime `event.action` produces
 * a union too wide for `sentence.values` to typecheck against. Routing every
 * branch through one function with a declared return type is what makes the
 * map's value type `AuditSentence` everywhere, not a per-branch guess.
 */
function sentence(key: TKey, values?: Record<string, unknown>): AuditSentence {
	return values === undefined ? { key } : { key, values }
}

type AuditSentenceBuilder = (event: AuditEvent, t: TFunction) => AuditSentence

/**
 * Action → the Cosa column's sentence, one per kind of act.
 * **Total by construction**, same reasoning as {@link AUDIT_ACTION_LABEL_KEYS}:
 * an action nothing here can render is a compile error rather than a blank
 * cell.
 *
 * Every sentence starts with the verb: the actor is the grammatical subject
 * and is already drawn in Chi, so naming them again here would say the same
 * thing twice on one row. A subject this builder can name — the invited
 * person, the share's recipient, the account whose session was revoked —
 * goes through the `b` component in the string so the cell can render it
 * `font-semibold`; `values` carries the raw pieces, translated where the
 * value is an enum (`role.org_admin`) and passed through where it already is
 * a name.
 *
 * Only what makes the sentence meaningful is read from `metadata` — the
 * server has already narrowed it to the catalogue's own allowlist for that
 * action, so nothing here reaches for a key the contract does not declare.
 * Facts that do not change the reading (`durationMs`, an internal `reason`
 * code) stay off the sentence and are still in the Sheet's own metadata list
 * for whoever wants them.
 */
export const AUDIT_ACTION_SENTENCE = {
	'authz.denied': () => sentence('admin-audit:sentence.authz_denied'),
	'policy.changed': (event) =>
		sentence('admin-audit:sentence.policy_changed', {
			key: metaString(event, 'key') ?? event.objectId
		}),
	'audit.retention_applied': (event) =>
		sentence('admin-audit:sentence.audit_retention_applied', {
			count: metaNumber(event, 'removed') ?? 0
		}),
	'share.granted': (event, t) =>
		sentence(
			metaString(event, 'via') === 'move'
				? 'admin-audit:sentence.share_granted_via_move'
				: 'admin-audit:sentence.share_granted',
			{
				subject: shareSubjectLabel(event, t),
				role: roleLabel(metaString(event, 'role'), t)
			}
		),
	'share.role_changed': (event, t) =>
		sentence(
			metaString(event, 'via') === 'move'
				? 'admin-audit:sentence.share_role_changed_via_move'
				: 'admin-audit:sentence.share_role_changed',
			{
				subject: shareSubjectLabel(event, t),
				previousRole: roleLabel(metaString(event, 'previousRole'), t),
				role: roleLabel(metaString(event, 'role'), t)
			}
		),
	'share.revoked': (event, t) =>
		sentence(
			metaString(event, 'via') === 'move'
				? 'admin-audit:sentence.share_revoked_via_move'
				: 'admin-audit:sentence.share_revoked',
			{
				subject: shareSubjectLabel(event, t)
			}
		),
	// A group's own rows carry no resolved name (`objectName` resolves people
	// only), so the name comes from the metadata the catalogue declares for
	// each: the name at the time, kept there because the row may be gone.
	'group.created': (event) =>
		sentence('admin-audit:sentence.group_created', {
			name: metaString(event, 'name') ?? event.objectId
		}),
	'group.updated': (event) => {
		const name = metaString(event, 'name') ?? event.objectId
		const previousName = metaString(event, 'previousName')
		return previousName !== undefined && previousName !== name
			? sentence('admin-audit:sentence.group_renamed', {
					previousName,
					name
				})
			: sentence(
					metaString(event, 'visibility') === 'private'
						? 'admin-audit:sentence.group_made_private'
						: 'admin-audit:sentence.group_made_public',
					{ name }
				)
	},
	'group.deleted': (event) =>
		sentence('admin-audit:sentence.group_deleted', {
			name: metaString(event, 'name') ?? event.objectId
		}),
	// The person is `metadata.userId` and nothing resolves a name for it, so
	// the sentence says what happened and the Sheet carries the identifiers.
	'group.member_added': () =>
		sentence('admin-audit:sentence.group_member_added'),
	'group.member_removed': () =>
		sentence('admin-audit:sentence.group_member_removed'),
	// None of these link events names the node: `create-link`/`update-link-expiry`/
	// `revoke-link` and the public page's open (`link.accessed`, OPSSTD-8805)
	// log no `name` in their metadata (see `audit/constants.ts`'s
	// catalogue entries), so there is nothing here to read that the server
	// itself did not choose to keep. No word for what the link allowed: every
	// link lets its visitor view and download (OPSSTD-8559). An older event
	// still carries `action` in its metadata, and the sentence no longer
	// reads it.
	'link.accessed': () => sentence('admin-audit:sentence.link_accessed'),
	// OPSSTD-9029. `newRole` null is «closed»; the node is the row's object, and
	// the metadata carries no name, as for the link events above.
	'org_link.changed': (event, t) => {
		const role = metaString(event, 'newRole')
		return role
			? sentence('admin-audit:sentence.org_link_opened_to', {
					role: roleLabel(role, t)
				})
			: sentence('admin-audit:sentence.org_link_closed')
	},
	'org_link.opened': () => sentence('admin-audit:sentence.org_link_visited'),
	'link.created': (event) =>
		sentence(
			metaBoolean(event, 'hasPassword')
				? 'admin-audit:sentence.link_created_password_protected'
				: 'admin-audit:sentence.link_created'
		),
	'link.updated': () => sentence('admin-audit:sentence.link_updated'),
	'link.revoked': () => sentence('admin-audit:sentence.link_revoked'),
	'link.previewed': (event) =>
		sentence('admin-audit:sentence.link_previewed', {
			name: metaString(event, 'name') ?? event.objectId
		}),
	'link.downloaded': (event) =>
		sentence('admin-audit:sentence.link_downloaded', {
			name: metaString(event, 'name') ?? event.objectId
		}),
	'link.password_locked': () =>
		sentence('admin-audit:sentence.link_password_locked'),
	'link.upload_limited': () =>
		sentence('admin-audit:sentence.link_upload_limited'),
	'upload.expired': (event) =>
		sentence('admin-audit:sentence.upload_expired', {
			count: metaNumber(event, 'abandonedForMinutes') ?? 0
		}),
	'node.upload_reconciled': () =>
		sentence('admin-audit:sentence.node_upload_reconciled'),
	// `node.trashed`, `node.restored` and `node.version_restored` are retired
	// (OPSSTD-8136): each is a `restore`/`trash`/`version_restore` batch row
	// now, rendered by the batch table's own sentence keys
	// ({@link AUDIT_BATCH_KIND_SENTENCE_KEYS}), not through this map.
	// `node.purged` names no file either, for the same reason it never did:
	// the row carries a count, never a name.
	'node.purged': () => sentence('admin-audit:sentence.node_purged'),
	// The version's number and never the file's name, for the reason
	// `node.purged` names none.
	'node.version_deleted': (event) =>
		sentence('admin-audit:sentence.node_version_deleted', {
			versionNumber: metaNumber(event, 'versionNumber') ?? 0
		}),
	// The sweep's one row per run: a count, never a file.
	'node.versions_expired': (event) =>
		sentence('admin-audit:sentence.node_versions_expired', {
			count: metaNumber(event, 'removed') ?? 0
		}),
	'user.invited': (event, t) =>
		sentence('admin-audit:sentence.user_invited', {
			name: personLabel(event.objectName, t),
			role: roleLabel(metaString(event, 'role'), t)
		}),
	'user.invite_accepted': (event, t) =>
		sentence('admin-audit:sentence.user_invite_accepted', {
			role: roleLabel(metaString(event, 'role'), t)
		}),
	'user.invite_resent': (event, t) =>
		sentence('admin-audit:sentence.user_invite_resent', {
			name: personLabel(event.objectName, t),
			count: metaNumber(event, 'resendCount') ?? 1
		}),
	'user.removed': (event, t) =>
		sentence('admin-audit:sentence.user_removed', {
			name: personLabel(event.objectName, t)
		}),
	'user.email_changed': (event, t) =>
		sentence('admin-audit:sentence.user_email_changed', {
			name: personLabel(event.objectName, t),
			previousEmail: metaString(event, 'previousEmail') ?? '',
			newEmail: metaString(event, 'newEmail') ?? ''
		}),
	'user.role_changed': (event, t) =>
		sentence('admin-audit:sentence.user_role_changed', {
			name: personLabel(event.objectName, t),
			previousRole: roleLabel(metaString(event, 'previousRole'), t),
			newRole: roleLabel(metaString(event, 'newRole'), t)
		}),
	// The platform operator's act, and the row says so: it is the one entry
	// in this category an administrator reading their own trail did not
	// perform and cannot perform.
	'user.owner_granted': (event, t) =>
		sentence('admin-audit:sentence.user_owner_granted', {
			name: personLabel(event.objectName, t),
			previousRole: roleLabel(metaString(event, 'previousRole'), t)
		}),
	// The other half, and it carries the number rather than a role: how many
	// owners the organization had left afterwards is the part a reader of
	// the trail cannot reconstruct from anything else on the row.
	'user.owner_revoked': (event, t) =>
		sentence('admin-audit:sentence.user_owner_revoked', {
			name: personLabel(event.objectName, t),
			remaining: metaString(event, 'remainingOwners') ?? '?'
		}),
	'user.quota_changed': (event, t) =>
		sentence('admin-audit:sentence.user_quota_changed', {
			name: personLabel(event.objectName, t),
			previous: quotaLabel(event, 'previousQuotaBytes', t),
			next: quotaLabel(event, 'newQuotaBytes', t)
		}),
	'organization.created': (event) =>
		sentence(
			event.outcome === 'failure'
				? 'admin-audit:sentence.organization_created_failure'
				: 'admin-audit:sentence.organization_created_success'
		),
	'organization.suspended': (event) => {
		const reason = metaString(event, 'reason')
		return reason
			? sentence('admin-audit:sentence.organization_suspended_reason', {
					reason
				})
			: sentence('admin-audit:sentence.organization_suspended')
	},
	'organization.reactivated': () =>
		sentence('admin-audit:sentence.organization_reactivated'),
	// The platform operator's act, as `organization.suspended` is: Chi names
	// the operator, so the sentence only says what moved.
	'organization.quota_changed': (event, t) =>
		sentence('admin-audit:sentence.organization_quota_changed', {
			previous: quotaLabel(event, 'previousQuotaBytes', t),
			next: quotaLabel(event, 'newQuotaBytes', t)
		}),
	'organization.version_retention_changed': () =>
		sentence('admin-audit:sentence.organization_version_retention_changed'),
	'organization.storage_destroyed': (event) =>
		sentence('admin-audit:sentence.organization_storage_destroyed', {
			count: metaNumber(event, 'nodesDeleted') ?? 0
		}),
	// Said by the person about themselves: nobody but the account's owner can
	// change its credentials, so there is no object to name.
	'user.credentials_changed': (event) =>
		sentence(
			CREDENTIAL_CHANGE_SENTENCE_KEYS[
				metaString(event, 'action') ?? ''
			] ?? 'admin-audit:sentence.user_credentials_changed.other'
		),
	'session.revoked': (event, t) =>
		sentence('admin-audit:sentence.session_revoked', {
			name: personLabel(event.objectName, t)
		}),
	'session.force_disconnected': (event, t) =>
		sentence('admin-audit:sentence.session_force_disconnected', {
			name: personLabel(event.objectName, t)
		}),
	'user.deactivated': (event, t) => {
		const reason = metaString(event, 'reason')
		const name = personLabel(event.objectName, t)
		return reason
			? sentence('admin-audit:sentence.user_deactivated_reason', {
					name,
					reason
				})
			: sentence('admin-audit:sentence.user_deactivated', { name })
	},
	'user.reactivated': (event, t) =>
		sentence('admin-audit:sentence.user_reactivated', {
			name: personLabel(event.objectName, t)
		}),
	'user.membership_removed': (event, t) =>
		sentence('admin-audit:sentence.user_membership_removed', {
			name: personLabel(event.objectName, t)
		})
} as const satisfies Record<AuditAction, AuditSentenceBuilder>

/** The Cosa column's content for one row. */
export function auditSentenceFor(event: AuditEvent, t: TFunction) {
	return AUDIT_ACTION_SENTENCE[event.action](event, t)
}

/**
 * The account action a `user.credentials_changed` event carries, as a
 * sentence. Keyed by the wire value; an action this list does not know yet
 * falls back to the generic sentence rather than to nothing.
 */
const CREDENTIAL_CHANGE_SENTENCE_KEYS: Partial<Record<string, TKey>> = {
	'update-password':
		'admin-audit:sentence.user_credentials_changed.update_password',
	'configure-otp':
		'admin-audit:sentence.user_credentials_changed.configure_otp',
	'regenerate-recovery-codes':
		'admin-audit:sentence.user_credentials_changed.regenerate_recovery_codes',
	'remove-otp-device':
		'admin-audit:sentence.user_credentials_changed.remove_otp_device'
}
